// useNhsApp.ts
import { computed, onMounted, ref } from 'vue';
import { loadNhsSdk, windowHasNhsSdk } from './loadNhsSdk';
import { useNhsAppStore } from '@/stores/useNhsAppStore';

export function useNhsApp(
  opts: { envBaseUrl?: string; cacheBust?: string; autoLoad?: boolean } = {},
) {
  const { envBaseUrl = 'https://www.nhsapp.service.nhs.uk', cacheBust, autoLoad = true } = opts;

  const ready = ref(false);
  const loadError = ref<Error | null>(null);

  async function ensureLoaded() {
    try {
      await loadNhsSdk(envBaseUrl, cacheBust);
      ready.value = true;
    } catch (e) {
      loadError.value = e as Error;
      ready.value = false;
    }
  }

  const nhsapp = computed(() => window.nhsapp);

  const testIsInApp = () => {
    console.log('windowHasNhsSdk', windowHasNhsSdk());
    console.log('getAppPlatform', window.nhsapp?.tools?.getAppPlatform());
    console.log('testIsInApp isOpenInNHSApp', window.nhsapp?.tools?.isOpenInNHSApp());
    console.log('testIsInApp isOpenInNhsApp.value', isOpenInNhsApp.value);
  };

  const isOpenInNhsApp = computed(() => {
    // Real NHS App
    console.log('isOpenInNHSApp', window.nhsapp?.tools?.isOpenInNHSApp());
    ready.value; // 👈 force dependency

    return window.nhsapp?.tools?.isOpenInNHSApp?.() ?? false;
  });

  function goBack(page, fallback?: () => void) {
    if (isOpenInNhsApp.value) {
      const nav = nhsapp.value!.navigation;
      nav.goToPage(nav.AppPage.GO_BACK);
      return;
    }
    fallback?.();
  }

  function setBackAction(backAction: () => void, fallback?: () => void) {
    if (isOpenInNhsApp.value) {
      const nav = nhsapp.value!.navigation;
      nav.setBackAction(backAction);
      return;
    }
    fallback?.();
  }

  function goToPage(
    page: keyof typeof window.nhsapp.navigation.AppPage | false,
    fallback?: () => void,
  ) {
    if (isOpenInNhsApp.value && page) {
      const nav = nhsapp.value!.navigation;
      nav.goToPage(nav.AppPage[page]);
      return;
    }
    fallback?.();
  }

  function goToHomepage(fallback?: () => void) {
    const nav = nhsapp.value?.navigation;
    if (isOpenInNhsApp.value && nav?.goToHomepage) {
      nav.goToHomepage();
      return;
    }
    fallback?.();
  }

  function openBrowserOverlay(uri: string, fallback?: () => void) {
    if (isOpenInNhsApp.value) {
      nhsapp.value!.navigation.openBrowserOverlay(uri);
      return;
    }
    fallback?.();
  }

  const nhsAppStore = useNhsAppStore();

  function syncNhsState(): void {
    console.log('syncNhsState getAppPlatform', window.nhsapp?.tools?.getAppPlatform());
    console.log('syncNhsState isOpenInNHSApp', window.nhsapp?.tools?.isOpenInNHSApp());
    nhsAppStore.setWindowHasNhsSdk(windowHasNhsSdk());
    nhsAppStore.setAppPlatform(window.nhsapp?.tools?.getAppPlatform());
    nhsAppStore.setIsOpenInNHSApp(window.nhsapp?.tools?.isOpenInNHSApp());
  }

  function resetNhsState(): void {
    nhsAppStore.resetNhsState();
  }

  onMounted(async () => {
    if (autoLoad) void (await ensureLoaded());
    syncNhsState();
  });

  return {
    ensureLoaded,
    goBack,
    goToHomepage,
    goToPage,
    isOpenInNhsApp,
    loadError,
    openBrowserOverlay,
    ready,
    resetNhsState,
    setBackAction,
    testIsInApp,
  };
}
